import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Leaf, Bell, LogOut, LayoutDashboard, Briefcase, ShieldCheck, Users, HandCoins, Settings2, ScrollText, ClipboardList, Radio, Ticket, ShieldAlert, UserCog } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

function LogoMark({ compact = false }) {
  return (
    <Link to="/" className="flex items-center gap-2 min-w-0 flex-shrink-0" data-testid="brand-logo">
      <div className="w-9 h-9 rounded-2xl bg-[var(--forest)] text-white grid place-items-center flex-shrink-0">
        <Leaf className="w-5 h-5" strokeWidth={2} />
      </div>
      <div className="leading-tight min-w-0">
        <div className="font-heading text-lg text-[var(--forest)]">CARING</div>
        <div className="text-[10px] eyebrow -mt-0.5 hidden sm:block">Referências</div>
      </div>
    </Link>
  );
}

const referrerNav = [
  { to: "/dashboard", label: "Painel", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/vacancies", label: "Vagas", icon: Briefcase, testId: "nav-vacancies" },
  { to: "/notifications", label: "Avisos", icon: Bell, testId: "nav-notifications" },
];
const adminNav = [
  { to: "/admin", label: "Painel", icon: LayoutDashboard, testId: "nav-admin" },
  { to: "/admin/vacancies", label: "Vagas", icon: Briefcase, testId: "nav-admin-vacancies" },
  { to: "/admin/candidates", label: "Candidatos", icon: ClipboardList, testId: "nav-admin-candidates" },
  { to: "/admin/referrers", label: "Referenciadores", icon: Users, testId: "nav-admin-referrers" },
  { to: "/admin/bonuses", label: "Prémios", icon: HandCoins, testId: "nav-admin-bonuses" },
  { to: "/admin/groups", label: "Grupos", icon: Radio, testId: "nav-admin-groups" },
  { to: "/admin/invitations", label: "Convites", icon: Ticket, testId: "nav-admin-invitations" },
  { to: "/admin/users", label: "Utilizadores", icon: UserCog, testId: "nav-admin-users" },
  { to: "/admin/config", label: "Definições", icon: Settings2, testId: "nav-admin-config" },
  { to: "/admin/reset", label: "Limpeza", icon: ShieldAlert, testId: "nav-admin-reset" },
  { to: "/admin/audit", label: "Auditoria", icon: ScrollText, testId: "nav-admin-audit" },
];

function NavPill({ item, unread }) {
  return (
    <NavLink
      to={item.to}
      data-testid={item.testId}
      end
      className={({ isActive }) =>
        `whitespace-nowrap flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
          isActive ? "bg-[var(--forest)] text-white" : "text-[var(--ink-soft)] hover:bg-[var(--muted)]"
        }`
      }
    >
      <item.icon className="w-4 h-4" />
      <span>{item.label}</span>
      {item.to === "/notifications" && unread > 0 && (
        <span data-testid="notifications-unread-badge" className="ml-1 bg-[var(--terracotta)] text-white text-[10px] px-1.5 py-0.5 rounded-full">{unread}</span>
      )}
    </NavLink>
  );
}

export default function AppShell({ children, admin = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = admin ? adminNav : referrerNav;
  const [unread, setUnread] = useState(0);
  const seenIdsRef = useRef(new Set());
  const primedRef = useRef(false);

  useEffect(() => {
    if (admin) return;
    let alive = true;

    const tick = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!alive) return;
        setUnread(data.filter((n) => !n.read).length);
        // On first load, prime the seen set silently (do not toast historical items).
        if (!primedRef.current) {
          data.forEach((n) => seenIdsRef.current.add(n.id));
          primedRef.current = true;
          return;
        }
        // Toast anything new since previous tick, in chronological order (oldest first).
        const fresh = data.filter((n) => !seenIdsRef.current.has(n.id));
        fresh.slice().reverse().forEach((n) => {
          seenIdsRef.current.add(n.id);
          const isApply = n.type === "candidate_apply";
          const showFn = isApply ? toast.success : toast.info;
          showFn(n.title, {
            description: n.message,
            action: n.link ? { label: "Abrir", onClick: () => navigate(n.link) } : undefined,
            duration: isApply ? 10000 : 6000,
          });
        });
      } catch {
        // ignore
      }
    };

    tick();
    const id = setInterval(tick, 20000);
    return () => { alive = false; clearInterval(id); };
  }, [admin, navigate]);

  return (
    <div className="min-h-screen bg-[var(--bone)]">
      <header className="glass-nav sticky top-0 z-40">
        {/* Row 1: brand + view switch + logout */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          <LogoMark />

          {/* Referrer inline nav (only 3 items — fits easily) */}
          {!admin && (
            <nav className="hidden md:flex items-center gap-1 ml-4">
              {nav.map((item) => <NavPill key={item.to} item={item} unread={unread} />)}
            </nav>
          )}

          <div className="ml-auto flex items-center gap-2 min-w-0">
            {!admin && user?.role === "admin" && (
              <Link to="/admin" className="pill-btn pill-ghost !py-2 !px-3 hidden sm:inline-flex" data-testid="switch-to-admin">
                <ShieldCheck className="w-4 h-4" /> Admin
              </Link>
            )}
            {admin && (
              <>
                <span className="chip chip-neutral hidden lg:inline-flex" data-testid="admin-mode-chip">
                  <ShieldCheck className="w-3 h-3" /> Modo administrador
                </span>
                <Link to="/dashboard" className="pill-btn pill-ghost !py-2 !px-3 hidden xl:inline-flex" data-testid="switch-to-referrer">
                  Vista referenciador
                </Link>
              </>
            )}

            {!admin && (
              <div className="hidden xl:flex flex-col items-end leading-tight min-w-0">
                <div className="text-sm font-semibold text-[var(--ink)] truncate max-w-[180px]">{user?.name}</div>
                <div className="text-[11px] text-[var(--ink-soft)] truncate max-w-[180px]">{user?.email}</div>
              </div>
            )}

            <button
              onClick={async () => { await logout(); navigate("/"); }}
              className="pill-btn pill-ghost !py-2 !px-3 flex-shrink-0"
              data-testid="logout-btn"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Sair</span>
            </button>
          </div>
        </div>

        {/* Row 2 (admin only, ≥lg): the admin nav on its own line so it never collides */}
        {admin && (
          <div className="hidden lg:block border-t border-[var(--border)] bg-white/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-1 overflow-x-auto">
              {nav.map((item) => <NavPill key={item.to} item={item} unread={unread} />)}
            </div>
          </div>
        )}

        {/* Mobile nav strip:
            - Admin: below lg (≥1024)
            - Referrer: below md (≥768) */}
        <div className={`${admin ? "lg:hidden" : "md:hidden"} border-t border-[var(--border)] px-4 py-2 overflow-x-auto flex gap-2`}>
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              data-testid={`${item.testId}-mobile`}
              className={({ isActive }) =>
                `whitespace-nowrap flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                  isActive ? "bg-[var(--forest)] text-white" : "bg-[var(--muted)] text-[var(--ink-soft)]"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">{children}</main>
    </div>
  );
}
